import { AdminTwoPanelLayout } from '@/components/admin/AdminTwoPanelLayout';
import { CopyProviderAppointmentTypesDialog } from '@/components/admin/CopyProviderAppointmentTypesDialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { trpc } from '@/utils/trpc';
import { useSuperAdmin } from '@/utils/use-super-admin';
import { useOrganization } from '@clerk/clerk-react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import type { RouterOutput } from '@trapeze/server';
import {
  AlertCircle,
  Calendar,
  Check,
  ChevronRight,
  Copy,
  Info,
  Loader2,
  Minus,
  Plus,
  RefreshCw,
  Save,
  Search,
  ShieldAlert,
  User,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/_protected/admin/provider-appointment-types')({
  component: ProviderAppointmentTypesPage,
});

type Provider = RouterOutput['admin']['getProvidersWithAppointmentTypes']['providers'][number];
type AppointmentType =
  RouterOutput['admin']['getProvidersWithAppointmentTypes']['appointmentTypes'][number];

interface PendingChange {
  type: 'add' | 'remove';
  appointmentType: AppointmentType;
}

function ProviderAppointmentTypesPage() {
  const { isSuperAdmin, isLoading: isAdminLoading } = useSuperAdmin();
  const { organization, isLoaded: orgLoaded } = useOrganization();
  const router = useRouter();

  // State
  const [selectedEhrSystem, setSelectedEhrSystem] = useState<'imedicware'>('imedicware');
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null);
  const [providerSearchTerm, setProviderSearchTerm] = useState('');
  const [appointmentTypeSearchTerm, setAppointmentTypeSearchTerm] = useState('');
  const [assignedTypes, setAssignedTypes] = useState<Set<string>>(new Set());
  const [originalAssignedTypes, setOriginalAssignedTypes] = useState<Set<string>>(new Set());
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<Map<string, PendingChange>>(new Map());

  // Use current organization ID from Clerk
  const currentOrganizationId = organization?.id;

  // Redirect non-super admins to dashboard
  useEffect(() => {
    if (!isAdminLoading && !isSuperAdmin) {
      router.navigate({ to: '/dashboard' });
    }
  }, [isAdminLoading, isSuperAdmin, router]);

  // tRPC queries
  const providersWithTypesQuery = trpc.admin.getProvidersWithAppointmentTypes.useQuery(
    { organizationId: currentOrganizationId || '', ehrSystem: selectedEhrSystem },
    { enabled: !!currentOrganizationId }
  );

  const providerTypesQuery = trpc.admin.getProviderAppointmentTypes.useQuery(
    {
      organizationId: currentOrganizationId || '',
      providerId: selectedProviderId || '',
    },
    { enabled: !!currentOrganizationId && !!selectedProviderId }
  );

  // tRPC mutations
  const setProviderTypesMutation = trpc.admin.setProviderAppointmentTypes.useMutation({
    onSuccess: () => {
      providersWithTypesQuery.refetch();
      providerTypesQuery.refetch();
      setPendingChanges(new Map());
      setShowConfirmDialog(false);
      toast.success('Provider appointment types updated successfully');
    },
    onError: (error) => {
      toast.error(`Failed to update provider appointment types: ${error.message}`);
    },
  });

  // Load provider's current appointment types when selected
  useEffect(() => {
    if (providerTypesQuery.data && selectedProviderId) {
      const assigned = new Set(
        providerTypesQuery.data.appointmentTypes
          .filter((at) => at.isAssigned)
          .map((at) => at.appointmentTypeId)
      );
      setAssignedTypes(assigned);
      setOriginalAssignedTypes(assigned);
      setPendingChanges(new Map());

      // Debug logging
      console.log('Provider appointment types loaded:', {
        providerId: selectedProviderId,
        providerName: providerTypesQuery.data.provider.name,
        totalTypes: providerTypesQuery.data.appointmentTypes.length,
        assignedCount: assigned.size,
        assignedTypes: providerTypesQuery.data.appointmentTypes.filter((at) => at.isAssigned),
      });
    }
  }, [providerTypesQuery.data, selectedProviderId]);

  // Calculate pending changes whenever assignedTypes changes
  useEffect(() => {
    if (!selectedProviderId) return;

    const changes = new Map<string, PendingChange>();
    const allTypes = providersWithTypesQuery.data?.allAppointmentTypes || [];

    for (const type of allTypes) {
      const wasAssigned = originalAssignedTypes.has(type.id);
      const isAssigned = assignedTypes.has(type.id);

      if (wasAssigned && !isAssigned) {
        changes.set(type.id, { type: 'remove', appointmentType: type });
      } else if (!wasAssigned && isAssigned) {
        changes.set(type.id, { type: 'add', appointmentType: type });
      }
    }

    setPendingChanges(changes);
  }, [assignedTypes, originalAssignedTypes, selectedProviderId, providersWithTypesQuery.data]);

  // Filtered lists
  const filteredProviders = useMemo(() => {
    const providers = providersWithTypesQuery.data?.providers || [];
    const searchLower = providerSearchTerm.toLowerCase();
    return providers.filter(
      (provider) =>
        provider.name.toLowerCase().includes(searchLower) ||
        provider.ehrId.toLowerCase().includes(searchLower)
    );
  }, [providersWithTypesQuery.data?.providers, providerSearchTerm]);

  const currentlyAssignedTypes = useMemo(() => {
    // Use ALL appointment types (including non-schedulable) for currently assigned
    const types = providersWithTypesQuery.data?.allAppointmentTypes || [];
    return types.filter((type) => assignedTypes.has(type.id));
  }, [providersWithTypesQuery.data?.allAppointmentTypes, assignedTypes]);

  const availableTypes = useMemo(() => {
    // Use ALL appointment types (including non-schedulable) for available to add
    const types = providersWithTypesQuery.data?.allAppointmentTypes || [];
    const searchLower = appointmentTypeSearchTerm.toLowerCase();
    return types.filter(
      (type) =>
        !assignedTypes.has(type.id) &&
        (type.name.toLowerCase().includes(searchLower) ||
          type.abbreviation.toLowerCase().includes(searchLower) ||
          type.ehrId.toLowerCase().includes(searchLower))
    );
  }, [providersWithTypesQuery.data?.allAppointmentTypes, assignedTypes, appointmentTypeSearchTerm]);

  // Get provider's appointment type count
  const getProviderTypeCount = (providerId: string) => {
    const assignments = providersWithTypesQuery.data?.assignments;
    if (!assignments) return 0;
    const providerAssignments = assignments[providerId];
    // Debug log
    if (providerId === selectedProviderId) {
      console.log('Provider type count:', {
        providerId,
        assignmentCount: providerAssignments?.length || 0,
        assignments: providerAssignments || [],
      });
    }
    return providerAssignments ? providerAssignments.length : 0;
  };

  const handleGoBack = () => {
    router.navigate({ to: '/admin' });
  };

  const handleProviderSelect = (providerId: string) => {
    if (pendingChanges.size > 0) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to switch providers?'
      );
      if (!confirmed) return;
    }
    setSelectedProviderId(providerId);
    setPendingChanges(new Map());
    // Reset assigned types to force proper reload
    setAssignedTypes(new Set());
    setOriginalAssignedTypes(new Set());
  };

  const handleAddType = (typeId: string) => {
    setAssignedTypes((prev) => {
      const newSet = new Set(prev);
      newSet.add(typeId);
      return newSet;
    });
  };

  const handleRemoveType = (typeId: string) => {
    setAssignedTypes((prev) => {
      const newSet = new Set(prev);
      newSet.delete(typeId);
      return newSet;
    });
  };

  const handleSaveChanges = () => {
    if (!selectedProviderId || !currentOrganizationId) return;

    setProviderTypesMutation.mutate({
      organizationId: currentOrganizationId,
      providerId: selectedProviderId,
      appointmentTypeIds: Array.from(assignedTypes),
    });
  };

  const handleReset = () => {
    setAssignedTypes(new Set(originalAssignedTypes));
    setPendingChanges(new Map());
  };

  const handleCopySuccess = () => {
    providersWithTypesQuery.refetch();
    providerTypesQuery.refetch();
    setPendingChanges(new Map());
  };

  const getDurationBadgeColor = (duration: string) => {
    switch (duration) {
      case 'short':
        return 'bg-blue-100 text-blue-700';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700';
      case 'long':
        return 'bg-orange-100 text-orange-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  // Loading state
  if (isAdminLoading || !orgLoaded) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  // Access denied for non-super admins
  if (!isSuperAdmin) {
    return (
      <div className="flex min-h-svh flex-col items-center justify-center gap-4 p-4">
        <div className="w-full max-w-md">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <ShieldAlert className="h-5 w-5" />
                Access Denied
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                This page is only available to super administrators. Please contact your
                administrator if you need access.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <AdminTwoPanelLayout
      title="Provider Appointment Types"
      description="Configure which appointment types each provider can schedule"
      icon={Calendar}
      isSuperAdminPage={true}
      sidebarWidth="narrow"
      sidebarContent={
        currentOrganizationId && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Providers</span>
                <Badge variant="secondary">{filteredProviders.length}</Badge>
              </CardTitle>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search providers..."
                  value={providerSearchTerm}
                  onChange={(e) => setProviderSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[600px]">
                <div className="space-y-1 p-4">
                  {providersWithTypesQuery.isLoading ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                  ) : filteredProviders.length === 0 ? (
                    <div className="text-center py-4 text-muted-foreground">No providers found</div>
                  ) : (
                    filteredProviders.map((provider) => {
                      const typeCount = getProviderTypeCount(provider.id);
                      const isSelected = selectedProviderId === provider.id;

                      return (
                        <div
                          key={provider.id}
                          className={`flex items-center gap-2 p-3 rounded-lg cursor-pointer transition-colors ${
                            isSelected ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                          }`}
                          onClick={() => handleProviderSelect(provider.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleProviderSelect(provider.id);
                            }
                          }}
                          // biome-ignore lint/a11y/useSemanticElements: Using div for complex interactive element
                          role="button"
                          tabIndex={0}
                        >
                          <User
                            className={`h-4 w-4 ${isSelected ? '' : 'text-muted-foreground'}`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className={`font-medium truncate ${isSelected ? '' : ''}`}>
                              {provider.name}
                            </div>
                            <div
                              className={`text-xs ${isSelected ? 'opacity-90' : 'text-muted-foreground'}`}
                            >
                              {typeCount} appointment type{typeCount !== 1 ? 's' : ''}
                            </div>
                          </div>
                          <ChevronRight
                            className={`h-4 w-4 ${isSelected ? '' : 'text-muted-foreground'}`}
                          />
                        </div>
                      );
                    })
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        )
      }
      mainContent={
        <div className="flex flex-col gap-4">
          {/* Organization & EHR System Info */}
          <Card>
            <CardHeader>
              <CardTitle>Organization & EHR System</CardTitle>
            </CardHeader>
            <CardContent className="flex items-center gap-4">
              <div className="flex-1">
                <div className="text-sm font-medium text-muted-foreground mb-1">Organization</div>
                <div className="text-lg font-semibold">
                  {organization?.name ?? 'Unknown Organization'}
                </div>
              </div>
              <div className="w-48">
                <div className="text-sm font-medium text-muted-foreground mb-1">EHR System</div>
                <Select
                  value={selectedEhrSystem}
                  onValueChange={(value) => setSelectedEhrSystem(value as 'imedicware')}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="imedicware">iMedicWare</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {!currentOrganizationId ? (
            <Card>
              <CardContent className="py-8">
                <p className="text-center text-muted-foreground">
                  Please select an organization to manage provider appointment types.
                </p>
              </CardContent>
            </Card>
          ) : !selectedProviderId ? (
            <Card>
              <CardContent className="py-12">
                <div className="text-center text-muted-foreground">
                  <User className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                  Select a provider to configure their appointment types
                </div>
              </CardContent>
            </Card>
          ) : providerTypesQuery.isLoading ? (
            <Card>
              <CardContent className="py-12">
                <div className="flex justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Provider Header */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <User className="h-5 w-5" />
                      {providerTypesQuery.data?.provider.name}
                    </span>
                    <div className="flex items-center gap-2">
                      {pendingChanges.size > 0 && (
                        <Badge variant="destructive">
                          {pendingChanges.size} unsaved change
                          {pendingChanges.size !== 1 ? 's' : ''}
                        </Badge>
                      )}
                      <Button onClick={() => setShowCopyDialog(true)} variant="outline" size="sm">
                        <Copy className="h-4 w-4 mr-2" />
                        Copy from...
                      </Button>
                    </div>
                  </CardTitle>
                  <CardDescription>
                    Configure which appointment types this provider can schedule
                  </CardDescription>
                </CardHeader>
              </Card>

              {/* Pending Changes Alert */}
              {pendingChanges.size > 0 && (
                <Alert className="border-yellow-200 bg-yellow-50">
                  <AlertCircle className="h-4 w-4 text-yellow-600" />
                  <AlertDescription>
                    <div className="flex items-center justify-between">
                      <div>
                        <strong className="text-yellow-900">Unsaved Changes:</strong>
                        <div className="mt-2 space-y-1">
                          {Array.from(pendingChanges.values())
                            .slice(0, 3)
                            .map((change) => (
                              <div key={change.appointmentType.id} className="text-sm">
                                {change.type === 'add' ? (
                                  <span className="text-green-700">
                                    <Plus className="h-3 w-3 inline mr-1" />
                                    Adding: {change.appointmentType.name}
                                  </span>
                                ) : (
                                  <span className="text-red-700">
                                    <Minus className="h-3 w-3 inline mr-1" />
                                    Removing: {change.appointmentType.name}
                                  </span>
                                )}
                              </div>
                            ))}
                          {pendingChanges.size > 3 && (
                            <div className="text-sm text-gray-500">
                              ...and {pendingChanges.size - 3} more
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={handleReset}>
                          <RefreshCw className="h-4 w-4 mr-1" />
                          Reset
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => setShowConfirmDialog(true)}
                          disabled={setProviderTypesMutation.isPending}
                        >
                          <Save className="h-4 w-4 mr-1" />
                          Save Changes
                        </Button>
                      </div>
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Currently Assigned Types */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <Check className="h-4 w-4 text-green-600" />
                        Currently Assigned
                      </span>
                      <Badge variant="secondary">{currentlyAssignedTypes.length}</Badge>
                    </CardTitle>
                    <CardDescription className="text-xs">
                      Appointment types we're authorized to schedule for this provider
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[400px]">
                      <div className="space-y-2">
                        {currentlyAssignedTypes.length === 0 ? (
                          <div className="text-center py-8 text-muted-foreground text-sm">
                            No appointment types assigned
                          </div>
                        ) : (
                          currentlyAssignedTypes.map((type) => {
                            const change = pendingChanges.get(type.id);
                            const isBeingRemoved = change?.type === 'remove';

                            return (
                              <div
                                key={type.id}
                                className={`flex items-center gap-2 p-2 rounded-lg border transition-all ${
                                  isBeingRemoved
                                    ? 'border-red-200 bg-red-50 opacity-75'
                                    : 'border-gray-200 hover:bg-muted/50'
                                }`}
                              >
                                <div className="flex-1">
                                  <div
                                    className={`font-medium text-sm ${
                                      isBeingRemoved ? 'text-red-700 line-through' : ''
                                    }`}
                                  >
                                    {type.name}
                                  </div>
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <span className="font-mono">{type.abbreviation}</span>
                                    <Badge
                                      variant="outline"
                                      className={`text-xs ${getDurationBadgeColor(type.durationCategory)}`}
                                    >
                                      {type.durationCategory}
                                    </Badge>
                                  </div>
                                </div>
                                <Button
                                  size="sm"
                                  variant={isBeingRemoved ? 'default' : 'destructive'}
                                  onClick={() =>
                                    isBeingRemoved
                                      ? handleAddType(type.id)
                                      : handleRemoveType(type.id)
                                  }
                                >
                                  {isBeingRemoved ? (
                                    <>
                                      <Plus className="h-3 w-3 mr-1" />
                                      Keep
                                    </>
                                  ) : (
                                    <>
                                      <Minus className="h-3 w-3 mr-1" />
                                      Remove
                                    </>
                                  )}
                                </Button>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>

                {/* Available Types to Add */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <Plus className="h-4 w-4 text-blue-600" />
                        Available to Add
                      </span>
                      <Badge variant="secondary">{availableTypes.length}</Badge>
                    </CardTitle>
                    <CardDescription className="text-xs">
                      Appointment types from your organization not yet assigned to this provider
                    </CardDescription>
                    <div className="relative mt-2">
                      <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        placeholder="Search appointment types..."
                        value={appointmentTypeSearchTerm}
                        onChange={(e) => setAppointmentTypeSearchTerm(e.target.value)}
                        className="pl-8 h-8"
                      />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[400px]">
                      <div className="space-y-2">
                        {availableTypes.length === 0 ? (
                          <div className="text-center py-8 text-muted-foreground text-sm">
                            {appointmentTypeSearchTerm
                              ? 'No matching appointment types found'
                              : 'All appointment types are assigned'}
                          </div>
                        ) : (
                          availableTypes.map((type) => {
                            const change = pendingChanges.get(type.id);
                            const isBeingAdded = change?.type === 'add';

                            return (
                              <div
                                key={type.id}
                                className={`flex items-center gap-2 p-2 rounded-lg border transition-all ${
                                  isBeingAdded
                                    ? 'border-green-200 bg-green-50'
                                    : 'border-gray-200 hover:bg-muted/50'
                                }`}
                              >
                                <div className="flex-1">
                                  <div
                                    className={`font-medium text-sm ${
                                      isBeingAdded ? 'text-green-700' : ''
                                    }`}
                                  >
                                    {type.name}
                                  </div>
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <span className="font-mono">{type.abbreviation}</span>
                                    <Badge
                                      variant="outline"
                                      className={`text-xs ${getDurationBadgeColor(type.durationCategory)}`}
                                    >
                                      {type.durationCategory}
                                    </Badge>
                                  </div>
                                </div>
                                <Button
                                  size="sm"
                                  variant={isBeingAdded ? 'destructive' : 'default'}
                                  onClick={() =>
                                    isBeingAdded
                                      ? handleRemoveType(type.id)
                                      : handleAddType(type.id)
                                  }
                                >
                                  {isBeingAdded ? (
                                    <>
                                      <X className="h-3 w-3 mr-1" />
                                      Cancel
                                    </>
                                  ) : (
                                    <>
                                      <Plus className="h-3 w-3 mr-1" />
                                      Add
                                    </>
                                  )}
                                </Button>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              </div>

              {/* Help Text */}
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  <strong>How to use:</strong> Click "Add" to assign appointment types to this
                  provider, or "Remove" to unassign them. Changes are highlighted in green
                  (additions) or red (removals). Remember to save your changes when you're done.
                </AlertDescription>
              </Alert>
            </>
          )}
        </div>
      }
    >
      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You're about to make the following changes to {providerTypesQuery.data?.provider.name}
              's appointment types:
              <div className="mt-4 space-y-2 max-h-[200px] overflow-y-auto">
                {Array.from(pendingChanges.values()).map((change) => (
                  <div key={change.appointmentType.id} className="text-sm flex items-center gap-2">
                    {change.type === 'add' ? (
                      <>
                        <Plus className="h-3 w-3 text-green-600" />
                        <span className="text-green-700">
                          Adding: {change.appointmentType.name}
                        </span>
                      </>
                    ) : (
                      <>
                        <Minus className="h-3 w-3 text-red-600" />
                        <span className="text-red-700">
                          Removing: {change.appointmentType.name}
                        </span>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSaveChanges}>Save Changes</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Copy Dialog */}
      <CopyProviderAppointmentTypesDialog
        open={showCopyDialog}
        onOpenChange={setShowCopyDialog}
        targetProvider={providerTypesQuery.data?.provider || null}
        allProviders={providersWithTypesQuery.data?.providers || []}
        organizationId={currentOrganizationId || ''}
        onSuccess={handleCopySuccess}
        getProviderTypeCount={getProviderTypeCount}
      />
    </AdminTwoPanelLayout>
  );
}
